import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Tooltip,
  IconButton,
  DialogTitle,
  Stack,
  CircularProgress,
  Box,
  Button,
  Typography,
  Chip,
  ListItem,
  Paper,
} from '@mui/material';
import { AssignmentIndRounded, CloseRounded } from '@mui/icons-material';

import { NewDialogActions, NewDialogContent, NewDialog, NewDialogTitle } from 'components';

import Form from './Form';
import graph from './graph';
import { SITE_ROLE_IDS, WORKSHOP_ROLE_IDS } from './Form/schema';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { isEmptyObject } from 'helpers/formatObject';
import { FormattedMessage } from 'react-intl';

export default function RoleAssignment({ ids, refetch, handleDelete }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const { userToken } = useSelector((state) => state.auth);

  const onOpen = () => {
    if (ids.length > 0) {
      return setOpen(true);
    }
    return toast.warning(<FormattedMessage id="selectItems" />);
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && ids.length === 0) {
      onClose();
    }
  }, [open, ids.length]);

  const [formCreate, { loading }] = useMutation(graph.assignRole.query, {
    context: {
      serviceName: graph.assignRole.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const onChange = ({ formData }) => {
    setFormData(formData);
  };

  const onSubmit = async () => {
    try {
      const { role_id, workshop_id, site_id } = formData;
      if (!role_id) {
        return toast.warning('لطفا نقش را انتخاب کنید');
      }
      const needsWorkshop = WORKSHOP_ROLE_IDS.includes(String(role_id));
      const needsSite = SITE_ROLE_IDS.includes(String(role_id));
      const selectedWorkshops = (Array.isArray(workshop_id) ? workshop_id : [workshop_id]).filter(
        (id) => id != null && id !== ''
      );
      if (needsWorkshop && selectedWorkshops.length === 0) {
        return toast.warning('لطفا کارگاه را انتخاب کنید');
      }
      if (needsSite && !site_id) {
        return toast.warning('لطفا سایت را انتخاب کنید');
      }
      const restFormData = { role_id, ...(needsSite ? { site_id } : {}) };
      const workshopIds = needsWorkshop ? selectedWorkshops : [undefined];
      const user_ids = ids.map((user) => user.id);
      let hasError = false;
      const messages = [];

      for (const workshopId of workshopIds) {
        const { data, errors } = await formCreate({
          variables: {
            user_ids,
            ...restFormData,
            ...(workshopId != null && workshopId !== '' ? { workshop_id: String(workshopId) } : {}),
          },
        });
        if (errors) {
          hasError = true;
          break;
        }
        if (!isEmptyObject(data)) {
          data[graph.assignRole.name]?.messages?.forEach((message) => messages.push(String(message)));
        }
      }

      if (!hasError) {
        refetch();
        onClose();
        setFormData({});
        messages.forEach((message) => toast.success(message));
      }
    } catch (error) {
      setFormData(formData);
    }
  };

  return (
    <>
      <Tooltip title="اختصاص نقش">
        <IconButton sx={{ bgcolor: 'action.selected' }} size="medium" color="error" onClick={onOpen}>
          {loading ? (
            <CircularProgress color="error" size={20} />
          ) : (
            <AssignmentIndRounded color="error" fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <NewDialog
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        dir="rtl"
        fullWidth
        maxWidth="xs"
      >
        <NewDialogTitle title={<FormattedMessage id="role_asignment" />} onClose={onClose} />
        <NewDialogContent>
          <Stack
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              listStyle: 'none',
              p: 0.5,
              m: 2,
            }}
            component="ul"
          >
            <Stack rowGap={0.5} sx={{ mb: 1, width: '100%' }}>
              {ids.map((model) => (
                <Paper key={model.id} variant="outlined">
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5 }}>
                    <Typography variant="body2" noWrap>
                      {model.title}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => handleDelete(model)}>
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Form formData={formData} loading={loading} onChange={onChange} />
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Button size="large" variant="contained" onClick={onSubmit}>
            <FormattedMessage id="confirm" />
          </Button>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
